import React, { useEffect, useRef, useState } from 'react';
import M_PER_NEW_01 from './M_PER_NEW_01';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import useFcmHandler from '../../../../Hooks/useFcmHandler';
import { M_PER_NEW_01_Schema } from './M_PER_NEW_01_Schema';
import {
  useSave1,
  useSearch1,
  useSearch2,
  useSearch4 as useSearchApprovalCandidates,
} from './M_PER_NEW_01_Query';
import { useSearch4 as useSearchRecentApprovalLine } from '../M_PER_NEW/M_PER_NEW_Query';
import { PermitActions } from '../../../../Reducer/PermitReducer';
import { useSave } from '../../../../Hooks/useSave';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import {
  buildDepApprovalFromRows,
  normalizeApprovalLine,
} from '../utils/approvalLine';
import { useBackStack } from '../../../../Hooks/back/useBackStack';
import useFileUpload from '../../../../Hooks/useFileUpload';
import Store from '../../../../Store/Store';
import dayjs from 'dayjs';
import Global from '../../../../Services/Common/Global';

export default function M_PER_NEW_01F() {
  const dispatch = useDispatch();
  const { id } = useParams(); // 수정 시 pmCd
  const location = useLocation();
  const searchDailyCd = String(
    location.state?.reuseDailyCd ??
      location.state?.dailyCd ??
      location.state?.DailyCd ??
      ''
  );
  const isReuseMode = location.state?.reuseMode === true;
  const idRef = useRef(id);
  const activeStep = useSelector((state) => state.common.activeStep);
  const { baseInfo, codeInfo, approvalLine, depApproval } = useSelector(
    (state) => state.permit
  );
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const sendFcm = useFcmHandler();
  const activeStepRef = useRef(activeStep);
  const [errors, setErrors] = useState({});
  const [recentApproverRows, setRecentApproverRows] = useState(null);

  const {
    createPreviewItem: createSignaturePreviewItem,
    SaveLogicFile: saveSignatureFile,
  } = useFileUpload({
    oldFilePathField: 'oldPath',
    newFileNameField: 'saveName',
    getTargetPath: () =>
      `Business/${userInfo.custCd}/SAF_BASIC_02/${userInfo.decUserId}`,
  });

  const {
    createPreviewItem: createAttachmentPreviewItem,
    SaveLogicFile: saveAttachmentFiles,
    DeleteLogicFile: deleteAttachmentFile,
    ConvertFileToPdf: convertAttachmentToPdf,
  } = useFileUpload({
    oldFilePathField: 'oldPath',
    newFileNameField: 'saveName',
    fileResultType: 'array',
    preserveOriginalDocuments: true,
    encryptUploadUserId: true,
    getTargetPath: () =>
      `Business/${userInfo.custCd}/PERMIT/${userInfo.decUserId}`,
  });

  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  const { run: SearchApprovalCandidates } = useSearchApprovalCandidates({
    onSuccess: (rows) => {
      const list = rows || [];
      dispatch(
        PermitActions.setDepApproval(buildDepApprovalFromRows(list, userInfo))
      );
    },
  });

  useEffect(() => {
    if (recentApproverRows === null || depApproval.length === 0) return;

    dispatch(
      PermitActions.initializeApprovalLine(
        normalizeApprovalLine(recentApproverRows, depApproval)
      )
    );
  }, [
    recentApproverRows,
    depApproval,
    dispatch,
  ]);

  // 페이지 진입 시 초기화 및 데이터 로드 (상세 Search2는 Search1 성공 후에만 실행)
  useEffect(() => {
    dispatch(CommonActions.setActiveStep(1));
    dispatch(PermitActions.resetPermitForm('NEW'));

    Search1();
    SearchApprovalCandidates();
  }, [id]);

  // 1. 상세 정보 조회 (수정 모드) — useSearch1 onSuccess보다 먼저 선언
  const { run: Search2 } = useSearch2({
    onSuccess: (response) => {
      const resData = Array.isArray(response) ? response[0] : response;
      if (!resData || !resData.mst) return;

      const mst = resData.mst;
      const firstDaily = resData.dailies?.[0] || {};
      const details = firstDaily.details || [];

      const selectedWorkItemIds = details.map((d) => String(d.workItemId));

      const allCheckedRisks = details
        .flatMap((d) => d.riskItems || [])
        .filter((i) => i.itemCheck === '0');
      const allCheckedEquips = details
        .flatMap((d) => d.equipItems || [])
        .filter((i) => i.itemCheck === '0');
      const allCheckedPreps = details
        .flatMap((d) => d.prepItems || [])
        .filter((i) => i.itemCheck === '0');

      const updateCheckList = (fullList, checkedItems, codeKey) =>
        (fullList || []).map((item) => {
          const isChecked = checkedItems?.some(
            (s) => String(s[codeKey]) === String(item[codeKey])
          );
          return { ...item, itemCheck: isChecked ? 'Y' : '' };
        });

      const latest = Store.getState().permit.codeInfo;

      dispatch(
        PermitActions.setCodeInfo({
          risks: updateCheckList(latest.risks, allCheckedRisks, 'riskCode'),
          equips: updateCheckList(
            latest.equips,
            allCheckedEquips,
            'workEquipCode'
          ),
          preparations: updateCheckList(
            latest.preparations,
            allCheckedPreps,
            'preparationCode'
          ),
        })
      );

      const selectedWorkTypeCodes =
        latest.workTypes
          ?.filter((wt) => selectedWorkItemIds.includes(String(wt.workItemId)))
          .map((wt) => wt.workType) || [];

      const remarkMap = {};
      const attachmentMap = {};
      details.forEach((d) => {
        remarkMap[String(d.workItemId)] = d.etcRemark || '';
        attachmentMap[String(d.workItemId)] = isReuseMode
          ? []
          : (d.files || []).map((file) => ({
              ...(file.originalName && {
                preview: ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
                  file.originalName.split('.').pop()?.toLowerCase()
                )
                  ? `${Global.file.useUrl}files/${file.filePath}`
                  : '',
              }),
              fileId: file.fileId,
              fileName: file.originalName,
              saveName: file.saveName,
              filePath: file.filePath,
              fileSize: Number(file.fileSize || 0),
              downloadUrl: `${Global.file.useUrl}files/${file.filePath}`,
              persisted: true,
            }));
      });

      const rawStartDate = mst.totalStartDate?.split(' ')[0] || '';
      const rawEndDate = mst.totalEndDate?.split(' ')[0] || '';
      const startTime = details[0]?.strTime || '09:00';
      const endTime = details[0]?.endTime || '18:00';
      const reuseDate = dayjs().add(1, 'day').format('YYYY-MM-DD');

      dispatch(
        PermitActions.setBaseInfo({
          ...mst,
          pmCd: isReuseMode ? '' : String(mst.pmCd),

          workTypes: selectedWorkTypeCodes,
          workItemIds: selectedWorkItemIds,

          workStartDate: `${isReuseMode ? reuseDate : rawStartDate} ${startTime}`,
          workEndDate: `${isReuseMode ? reuseDate : rawEndDate} ${endTime}`,

          workPlace: mst.workLoc,
          workDetail: mst.workContent,
          workList: mst.workerList,
          compNm: mst.partnerNm,
          mgrNm: mst.partnerManager,
          mgrTel: mst.partnerTel,

          etcRemarks: remarkMap,
          attachments: isReuseMode ? {} : attachmentMap,
        })
      );
    },
  });

  const { refetch: Search1 } = useSearch1({
    onSuccess: (rows) => {
      const rawData = rows[0];
      if (!rawData) return;

      const workTypes = rawData.workTypes || [];
      const mapData = (list) =>
        (list || []).map((item) => {
          const parent = workTypes.find(
            (w) => w.workItemId === item.workItemId
          );
          return {
            ...item,
            workType: parent ? parent.workType : '',
            itemCheck: '',
          };
        });

      dispatch(
        PermitActions.setCodeInfo({
          workTypes: workTypes,
          risks: mapData(rawData.riskCodes),
          equips: mapData(rawData.equipCodes),
          preparations: mapData(rawData.preparationCodes),
        })
      );

      const pmCd = idRef.current;
      if (pmCd || isReuseMode) {
        if (!searchDailyCd) {
          dispatch(
            showSnackbar({
              message:
                '일일 작업 정보가 없어 수정 데이터를 불러올 수 없습니다. 목록에서 다시 진입해주세요.',
              type: 'warning',
            })
          );
          return;
        }

        Search2({ dailyCd: searchDailyCd });
      }
    },
  });

  // 최근 결재선 자동 세팅
  useSearchRecentApprovalLine({
    onSuccess: (rows) => {
      setRecentApproverRows(rows ?? []);
    },
  });

  const { mutateAsync: save1Async } = useSave1();
  const { run: Save1 } = useSave({
    menuId: 'M_PER_NEW_01',
    schema: M_PER_NEW_01_Schema,
    saveData: (params) => ({ ...params, baseInfo, codeInfo, approvalLine }),

    fileSave: async (requestRows, params) => {
      const { signFile } = params;
      let reqSignPath = '';
      if (signFile) {
        const fileResult = await createSignaturePreviewItem(signFile);
        const finalPath = await saveSignatureFile([fileResult]);
        if (finalPath) reqSignPath = finalPath;
      }

      const savedAttachmentMap = {};
      for (const [workItemId, files] of Object.entries(
        baseInfo.attachments || {}
      )) {
        const existingFiles = (files || []).filter(
          (file) => file.persisted && file.filePath
        );
        const previewFiles = (files || []).filter(
          (file) => !file.persisted && file.previewFilePath
        );
        const movedFiles = previewFiles.length
          ? await saveAttachmentFiles(previewFiles)
          : [];

        if (movedFiles === null) {
          throw new Error('첨부파일 저장에 실패했습니다.');
        }
        savedAttachmentMap[workItemId] = [
          ...existingFiles,
          ...(movedFiles || []).map((file) => ({
            ...file,
            persisted: true,
          })),
        ];
      }

      return {
        reqSignPath,
        baseInfo: { ...baseInfo, attachments: savedAttachmentMap },
      };
    },

    requestSave: async (rows) => await save1Async(rows[0]),
    showSuccess: false,
    onSuccess: async ({ saveDetailData, userInfo }) => {
      const serverDailyCd =
        saveDetailData?.[0]?.dailyCd ?? saveDetailData?.[0]?.DailyCd ?? '';
      const heads = approvalLine?.depHeads || [];

      for (const head of heads) {
        await sendFcm({
          targetUserId: head.headId,
          requestUserId: userInfo.userid,
          title: '작업허가서 요청',
          body: '새로운 작업 허가서 승인 요청이 있습니다.',
          status: '요청',
          codeGroup: 'AL_002_001',
          route: '/M_Permit',
          responseData: String(serverDailyCd),
        });
      }

      dispatch(PermitActions.setMode('write'));
      dispatch(BottomSheetActions.CloseBottomSheet());
      dispatch(CommonActions.setActiveStep(8));
      dispatch(
        showSnackbar({ message: '요청이 완료되었습니다.', type: 'success' })
      );
    },
  });
  const handleChange = (key, value) => {
    dispatch(PermitActions.setBaseInfo({ [key]: value }));

    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const handleAttachmentFiles = async (workItemId, fileList) => {
    const selectedFiles = Array.from(fileList || []);
    if (selectedFiles.length === 0) return;

    const currentMap = Store.getState().permit.baseInfo.attachments || {};
    const currentFiles = Object.values(currentMap).flat();
    const nextCount = currentFiles.length + selectedFiles.length;
    const nextSize =
      currentFiles.reduce((sum, file) => sum + Number(file.fileSize || 0), 0) +
      selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const oversized = selectedFiles.find(
      (file) => file.size > 20 * 1024 * 1024
    );

    if (nextCount > 10) {
      dispatch(
        showSnackbar({
          message: '첨부파일은 작업허가서당 최대 10개까지 등록할 수 있습니다.',
          type: 'warning',
        })
      );
      return;
    }
    if (oversized) {
      dispatch(
        showSnackbar({
          message: `${oversized.name} 파일이 20MB를 초과했습니다.`,
          type: 'warning',
        })
      );
      return;
    }
    if (nextSize > 100 * 1024 * 1024) {
      dispatch(
        showSnackbar({
          message: '첨부파일 전체 용량은 최대 100MB입니다.',
          type: 'warning',
        })
      );
      return;
    }

    const uploaded = [];
    for (const file of selectedFiles) {
      const previewItem = await createAttachmentPreviewItem(file, {
        extra: { fileSize: file.size },
      });
      if (previewItem) uploaded.push(previewItem);
    }

    if (uploaded.length > 0) {
      handleChange('attachments', {
        ...currentMap,
        [workItemId]: [...(currentMap[workItemId] || []), ...uploaded],
      });
    }
  };

  const handleRemoveAttachment = async (workItemId, index) => {
    const currentMap = Store.getState().permit.baseInfo.attachments || {};
    const currentFiles = [...(currentMap[workItemId] || [])];
    const [removed] = currentFiles.splice(index, 1);

    if (removed?.previewFilePath && !removed.persisted) {
      await deleteAttachmentFile(removed.previewFilePath);
    }

    handleChange('attachments', {
      ...currentMap,
      [workItemId]: currentFiles,
    });
  };

  const resolveAttachmentPreview = async (file) => {
    if (file?.preview) return file.preview;
    const fileName = file?.fileName || file?.originalName || file?.saveName || '';
    const ext = fileName.split('.').pop()?.toLowerCase();
    const officeExts = ['hwp', 'hwpx', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

    const sourcePath = file?.filePath || file?.previewFilePath;
    if (officeExts.includes(ext) && sourcePath) {
      const convertedPath = await convertAttachmentToPdf(sourcePath);
      if (!convertedPath) return '';
      return /^https?:\/\//i.test(convertedPath)
        ? convertedPath
        : `${Global.file.useUrl}files/${convertedPath}`;
    }

    return sourcePath
      ? `${Global.file.useUrl}files/${sourcePath}`
      : '';
  };

  const ValidateAll = () => {
    const fields = M_PER_NEW_01_Schema.fields.baseInfo;
    const newErrors = {};
    let firstErrorMessage = '';

    for (const field in fields) {
      const meta = fields[field];

      if (meta.required !== 'Y' || meta.step !== 1) continue;

      if (!baseInfo[field] || String(baseInfo[field]).trim() === '') {
        newErrors[field] = `${meta.label}은(는) 필수입니다.`;

        if (!firstErrorMessage) {
          firstErrorMessage = newErrors[field];
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      dispatch(
        showSnackbar({
          message: firstErrorMessage,
          type: 'error',
        })
      );
      return false;
    }

    setErrors({});
    return true;
  };

  useBackStack({
    type: 'sheet',
    enabled: !bottomSheetState.bottom && activeStep < 8,
    onBack: () => {
      if (activeStepRef.current === 1) {
        dispatch(BottomSheetActions.OpenBottomSheet({ type: 'backClick' }));
      } else {
        dispatch(CommonActions.decreaseStep());
      }
    },
  });

  return (
    <div className="wrap">
      <M_PER_NEW_01
        id={id}
        Save1={Save1}
        handleChange={handleChange}
        ValidateAll={ValidateAll}
        errors={errors}
        onAttachFiles={handleAttachmentFiles}
        onRemoveAttachment={handleRemoveAttachment}
        onResolveAttachmentPreview={resolveAttachmentPreview}
      />
    </div>
  );
}
